import { Inject, Injectable } from "@angular/core";
import { AppEventId, AuthRequestEvent } from "@loader-event-bus/app-event.model";
import { APP_CONFIG, AppConfig, AppHostService, PersistentStore } from "app/core";
import type { PersistedUser } from "app/user";
import { USER_PERSISTENT_STORE, USER_STORE, UserStore } from "app/user";
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  delay,
  filter,
  finalize,
  map,
  mergeMap,
  of,
  take,
  tap,
} from "rxjs";
import type { AuthRequestMode } from "../../../loader-factory/src/models/auth.model";
import { AuthAdapter } from "./auth.adapter";
import { AuthApiClient } from "./auth.apiclient";
import { AuthStore } from "./auth.store";

/** Coordinates loader authentication, backend verification, and user-session state. */
@Injectable({ providedIn: "root" })
export class AuthService {
  private activeRequestId: string | null = null;
  private activeRequestMode: AuthRequestMode | null = null;
  private activeSubscription: Subscription | null = null;
  private verificationSubscription: Subscription | null = null;
  private hasInitialized = false;

  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
    private readonly appHostService: AppHostService,
    private readonly authApiClient: AuthApiClient,
    private readonly authAdapter: AuthAdapter,
    private readonly authStore: AuthStore,
    @Inject(USER_STORE) private readonly userStore: UserStore,
    @Inject(USER_PERSISTENT_STORE)
    private readonly userPersistentStore: PersistentStore<PersistedUser>
  ) {}

  /** Verifies the persisted session when the current application requires authentication. */
  public initialize(): void {
    if (this.hasInitialized) {
      return;
    }
    this.hasInitialized = true;

    if (!this.isAuthRequired()) {
      this.authStore.markNotRequired();
      return;
    }
    this.verifySession(
      this.userPersistentStore.get().pipe(map((persisted) => persisted?.sessionId ?? ""))
    );
  }

  /** Re-verifies the in-memory session id when a new conversation starts without a page reload. */
  public reverifySession(): void {
    if (!this.isAuthRequired()) {
      return;
    }
    this.verifySession(of(this.userStore.get().sessionId ?? ""));
  }

  /** @returns Whether this application is configured for OIDC authentication. */
  public isAuthRequired(): boolean {
    return this.appConfig.auth !== undefined && this.appConfig.authType === "oidc";
  }

  /** Cancels any in-flight authentication attempt and marks it as cancelled. */
  public cancelPendingAuthentication(): void {
    if (this.activeRequestId === null) {
      return;
    }
    const mode = this.activeRequestMode ?? "interactive";
    this.activeSubscription?.unsubscribe();
    this.authStore.markFailure(mode, "cancelled");
  }

  /** Requests authentication from the loader unless another attempt is already active. */
  public authenticate(mode: AuthRequestMode): void {
    if (this.activeRequestId !== null || this.authStore.isPending()) {
      return;
    }

    if (!this.appConfig.auth) {
      this.authStore.markNotRequired();
      return;
    }

    const requestId = crypto.randomUUID();
    this.activeRequestId = requestId;
    this.activeRequestMode = mode;
    this.authStore.startAuthentication(mode);

    this.activeSubscription = this.appHostService.authResult$
      .pipe(
        filter((result) => result.requestId === requestId),
        take(1),
        mergeMap((result) => {
          if (result.outcome === "failure") {
            this.authStore.markFailure(mode, result.reason, result.detail);
            return EMPTY;
          }

          return this.authApiClient.verify(result.sessionId).pipe(
            map((apiModel) => this.authAdapter.toUser(apiModel, result.sessionId)),
            mergeMap(({ user, persistedUser }) => {
              if (user.authenticated !== true) {
                this.userStore.set({ authenticated: false });
                this.authStore.markFailure(mode, "verification-failed");
                return EMPTY;
              }
              return this.persistUser(persistedUser).pipe(
                tap(() => this.userStore.set(user)),
                tap(() => this.authStore.markAuthenticated(user))
              );
            })
          );
        }),
        catchError(() => {
          this.authStore.markFailure(mode, "verification-failed");
          return EMPTY;
        }),
        finalize(() => {
          if (this.activeRequestId === requestId) {
            this.activeRequestId = null;
            this.activeRequestMode = null;
            this.activeSubscription = null;
          }
        })
      )
      .subscribe();

    const authRequest: AuthRequestEvent = {
      eventId: AppEventId.AUTH_REQUEST,
      data: { requestId, mode },
    };
    this.appHostService.send(authRequest);
  }

  /** Verifies a session id; restores the user, or re-authenticates when it is missing or stale. */
  private verifySession(sessionId$: Observable<string>): void {
    this.verificationSubscription?.unsubscribe();
    this.authStore.startVerification();
    this.verificationSubscription = sessionId$
      .pipe(
        take(1),
        mergeMap((sessionId) =>
          sessionId === ""
            ? of(null)
            : this.authApiClient
                .verify(sessionId)
                .pipe(map((apiModel) => this.authAdapter.toUser(apiModel, sessionId).user))
        ),
        tap((user) => {
          if (user?.authenticated === true) {
            this.userStore.set(user);
            this.authStore.markAuthenticated(user);
          } else {
            this.restartAuthentication();
          }
        }),
        catchError(() => {
          this.authStore.markFailure("silent", "verification-failed");
          return EMPTY;
        })
      )
      .subscribe();
  }

  /** Forgets the in-memory session and requests a new one; a success overwrites the stored id. */
  private restartAuthentication(): void {
    this.userStore.set({ authenticated: false });
    this.authenticate("silent");
  }

  /** Persists a verified user after the initial external-store read has completed. */
  private persistUser(persistedUser: PersistedUser): Observable<PersistedUser | null> {
    return this.userPersistentStore.get().pipe(
      take(1),
      delay(0),
      tap(() => this.userPersistentStore.set(persistedUser))
    );
  }
}
